import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Paper,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HeroSection from '../components/common/HeroSection';
import SectionTitle from '../components/common/SectionTitle';
import SchoolIcon from '@mui/icons-material/School';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import VerifiedIcon from '@mui/icons-material/Verified';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import { tutors } from '../data/tutors';

const avatarColors = ['primary.main', 'secondary.main', '#7B1FA2', '#E53935'];

const TutorsPage = () => {
  return (
    <Box>
      <HeroSection
        title="Our Tutors"
        subtitle="Meet the dedicated educators behind Xing Fan"
      />

      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <SectionTitle
            title="Our Teaching Team"
            subtitle="Experienced and passionate educators committed to your child's success"
          />

          {tutors.map((tutor, index) => (
            <Box key={tutor.id} sx={{ mb: index < tutors.length - 1 ? 8 : 0 }}>
              {index > 0 && (
                <Divider sx={{ mb: 8, borderColor: 'rgba(0,0,0,0.08)' }} />
              )}
              <Grid container spacing={4} alignItems="flex-start">
                {/* Tutor Photo & Quick Info */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ textAlign: 'center', p: 4 }}>
                    <Avatar
                      sx={{
                        width: 180,
                        height: 180,
                        mx: 'auto',
                        mb: 3,
                        border: '4px solid',
                        borderColor: index % 2 === 0 ? 'primary.light' : 'secondary.light',
                        boxShadow: index % 2 === 0
                          ? '0 8px 30px rgba(255,111,0,0.2)'
                          : '0 8px 30px rgba(38,166,154,0.2)',
                        bgcolor: avatarColors[index % avatarColors.length],
                        fontSize: '3rem',
                        fontWeight: 700,
                      }}
                    >
                      {tutor.nameZh.charAt(0)}
                    </Avatar>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {tutor.name}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: 'text.secondary', mb: 2, fontWeight: 400 }}
                    >
                      {tutor.nameZh}
                    </Typography>

                    <Chip
                      icon={<SchoolIcon />}
                      label={tutor.subject}
                      sx={{
                        bgcolor: index % 2 === 0 ? 'primary.main' : 'secondary.main',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        mb: 3,
                        maxWidth: '100%',
                        '& .MuiChip-label': {
                          whiteSpace: 'normal',
                          lineHeight: 1.4,
                          py: 0.5,
                        },
                      }}
                    />

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'flex-start', px: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon sx={{ fontSize: 20, color: index % 2 === 0 ? 'primary.main' : 'secondary.main' }} />
                        <Typography variant="body2">{tutor.phone}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon sx={{ fontSize: 20, color: index % 2 === 0 ? 'primary.main' : 'secondary.main' }} />
                        <Typography variant="body2">{tutor.email}</Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>

                {/* Tutor Details */}
                <Grid item xs={12} md={8}>
                  {/* Bio - English */}
                  <Paper sx={{ p: 4, mb: 3, borderRadius: 3 }}>
                    <SectionTitle title="About" align="left" />
                    <Typography
                      variant="body1"
                      sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 2 }}
                    >
                      {tutor.bio}
                    </Typography>
                    {tutor.bioZh && (
                      <Typography
                        variant="body1"
                        sx={{ lineHeight: 1.8, color: 'text.secondary', fontStyle: 'italic' }}
                      >
                        {tutor.bioZh}
                      </Typography>
                    )}
                  </Paper>

                  {/* Experience & Qualifications */}
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <WorkHistoryIcon sx={{ color: index % 2 === 0 ? 'primary.main' : 'secondary.main' }} />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Experience
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                          {tutor.experience}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {tutor.experienceZh}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <VerifiedIcon sx={{ color: index % 2 === 0 ? 'secondary.main' : 'primary.main' }} />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Qualifications
                          </Typography>
                        </Box>
                        <List dense disablePadding>
                          {tutor.qualifications.map((q, idx) => (
                            <ListItem key={idx} disableGutters sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                <VerifiedIcon sx={{ fontSize: 16, color: index % 2 === 0 ? 'secondary.main' : 'primary.main' }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={q}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Teaching Philosophy */}
                  <Paper
                    sx={{
                      p: 4,
                      mt: 3,
                      borderRadius: 3,
                      bgcolor: index % 2 === 0 ? 'rgba(255,111,0,0.04)' : 'rgba(38,166,154,0.04)',
                      borderLeft: '4px solid',
                      borderColor: index % 2 === 0 ? 'primary.main' : 'secondary.main',
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <FormatQuoteIcon
                        sx={{ fontSize: 40, color: index % 2 === 0 ? 'primary.light' : 'secondary.light', flexShrink: 0 }}
                      />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          Teaching Philosophy
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontStyle: 'italic',
                            lineHeight: 1.8,
                            color: 'text.secondary',
                            mb: 1,
                          }}
                        >
                          &ldquo;{tutor.philosophy}&rdquo;
                        </Typography>
                        {tutor.philosophyZh && (
                          <Typography
                            variant="body1"
                            sx={{
                              fontStyle: 'italic',
                              lineHeight: 1.8,
                              color: 'text.secondary',
                            }}
                          >
                            &ldquo;{tutor.philosophyZh}&rdquo;
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          ))}

          {/* CTA */}
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/contact"
              startIcon={<ContactMailIcon />}
            >
              Get in Touch
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default TutorsPage;
